<template>
  <div class="app">
    <Header />
    <div class="container">
      <Controls 
        :form-data="formData"
        :models="models"
        :selected-models="selectedModels"
        :loading="loading"
        @update-form="updateFormData"
        @toggle-model="toggleModel"
        @run-tests="runTests"
      />
      
      <LoadingSpinner v-if="loading" />
      
      <ErrorMessage v-if="error" :error="error" />
      
      <Results 
        v-if="results.length > 0 || Object.keys(modelStates).length > 0"
        :results="results"
        :models="models"
        :model-states="modelStates"
        :selected-models="selectedModels"
      />
    </div>
    
  </div>
</template>

<script>
import { reactive } from 'vue'
import Header from './components/Header.vue'
import Controls from './components/Controls.vue'
import LoadingSpinner from './components/LoadingSpinner.vue'
import ErrorMessage from './components/ErrorMessage.vue'
import Results from './components/Results.vue'
import { testModel } from './services/api.js'

export default {
  name: 'App',
  components: {
    Header,
    Controls,
    LoadingSpinner,
    ErrorMessage,
    Results,
  },
  data() {
    return {
      formData: {
        project_id: "201",
        custom_prompt: "",
        mood_and_tone: "Professional",
        target_audiences: "Investors",
        model_settings: {}
      },
      models: [
        // OpenAI Models
        // {
        //   id: "gpt-4.1-nano",
        //   name: "GPT-4.1 Nano",
        //   description: "Best choice - fastest speed, lowest cost, premium quality",
        //   costPerRequest: "$0.004",
        //   speed: "up to 5.23s",
        //   recommended: true,
        //   provider: "OpenAI"
        // },
        // Google Gemini Models
        {
          id: "gemini-2.5-flash-lite",
          name: "Gemini 2.5 Flash Lite",
          description: "Ultra-fast and cost-effective for simple tasks",
          costPerRequest: "$0.001",
          speed: "up to 2.5s",
          recommended: true,
          provider: "Google"
        },
        {
          id: "gemini-2.5-flash",
          name: "Gemini 2.5 Flash",
          description: "Ultra-fast and cost-effective for simple tasks",
          costPerRequest: "$0.001",
          speed: "up to 2.5s",
          recommended: true,
          provider: "Google"
        },
        {
          id: "gemini-2.5-pro",
          name: "Gemini 2.5 Pro",
          description: "Ultra-fast and cost-effective for simple tasks",
          costPerRequest: "$0.001",
          speed: "up to 2.5s",
          recommended: true,
          provider: "Google"
        }
      ],
      selectedModels: ["gemini-2.5-flash-lite"],
      loading: false,
      results: [],
      modelStates: reactive({}), // Use reactive for deep reactivity
      error: null
    }
  },
  computed: {
    groupedModels() {
      const grouped = {}
      this.models.forEach(model => {
        if (!grouped[model.provider]) {
          grouped[model.provider] = []
        }
        grouped[model.provider].push(model)
      })
      return grouped
    }
  },
  methods: {
    updateFormData(newData) {
      this.formData = { ...this.formData, ...newData }
    },
    toggleModel(modelId) {
      const index = this.selectedModels.indexOf(modelId)
      if (index > -1) {
        this.selectedModels.splice(index, 1)
      } else {
        this.selectedModels.push(modelId)
      }
    },
    async runTests() {
      if (this.selectedModels.length === 0) return
      
      this.loading = true
      this.error = null
      this.results = []
      
      // Clear previous states and initialize new ones
      Object.keys(this.modelStates).forEach(key => {
        delete this.modelStates[key]
      })
      
      // Initialize model states
      this.selectedModels.forEach(modelId => {
        this.modelStates[modelId] = {
          status: 'loading',
          startTime: Date.now(),
          error: null,
          result: null
        }
      })
      
      // Start all tests simultaneously but handle them individually
      this.selectedModels.forEach(modelId => this.testModelIndividually(modelId))
    },

    async testModelIndividually(modelId) {
      try {
        console.log(`🚀 Starting test for ${modelId}`)
        
        const result = await testModel(this.formData, modelId)
        
        // Extract cost info if available
        const costInfo = result.cost_info || result.cost_summary || null
        
        // Update individual model state - direct assignment works with reactive
        this.modelStates[modelId] = {
          status: 'completed',
          startTime: this.modelStates[modelId].startTime,
          duration: Date.now() - this.modelStates[modelId].startTime,
          error: null,
          result: result,
          costInfo: costInfo // Add this line
        }
        
        // Add to results array for display
        this.results.push({
          model: modelId,
          response: result,
          success: true,
          duration: this.modelStates[modelId].duration,
          costInfo: costInfo // Add this line
        })
        
        console.log(`✅ Completed test for ${modelId}`)
        this.checkAllTestsComplete()
        
      }  catch (error) {
        console.error(`❌ Failed test for ${modelId}:`, error.message)
        
        // Update individual model state with error
        this.modelStates[modelId] = {
          status: 'error',
          startTime: this.modelStates[modelId].startTime,
          duration: Date.now() - this.modelStates[modelId].startTime,
          error: error.message,
          result: null
        }
        
        // Add error result
        this.results.push({
          model: modelId,
          response: { error: error.message },
          success: false,
          duration: this.modelStates[modelId].duration
        })
        
        // Check if all tests are done
        this.checkAllTestsComplete()
      }
    },

    checkAllTestsComplete() {
      const allStates = Object.values(this.modelStates)
      const allComplete = allStates.every(state => 
        state.status === 'completed' || state.status === 'error'
      )
      
      if (allComplete) {
        this.loading = false
        console.log('🎯 All model tests completed')
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  color: #333;
}

.app {
  min-height: 100vh;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
}

@media (max-width: 768px) {
  .container {
    padding: 0 10px 10px 10px;
  }
}
</style>